import { useNavigate } from 'react-router-dom'
import { BodyText } from 'styling/Typography'
import { IOpsWaterfallBar } from 'types/opsAnalytics.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { LOSS_BUCKET_META } from '../Shared/lossClassifier'
import {
  BucketTag,
  LabelCell,
  OpenHint,
  Segment,
  Track,
  ValueCol,
  ValueText,
  WaterfallRow,
  WaterfallWrap,
} from './LossWaterfall.styled'

const UNATTRIBUTED_TOOLTIP =
  'Variance not yet attributed to a cause — shown on purpose so the named numbers stay honest.'

const tonnesLabel = (tonnes: number): string =>
  tonnes < 0 ? `−${Math.abs(tonnes).toLocaleString('en-US')} t` : `${tonnes.toLocaleString('en-US')} t`

const barTone = (bar: IOpsWaterfallBar, target: number): IStatusPill['status'] => {
  if (bar.bucket === 'TARGET') return 'info'
  if (bar.bucket === 'ACTUAL') return bar.tonnes >= target ? 'success' : 'warning'
  if (bar.bucket === 'UNATTRIBUTED') return 'neutral'
  return LOSS_BUCKET_META[bar.bucket].tone
}

const withResidual = (bars: IOpsWaterfallBar[]): IOpsWaterfallBar[] => {
  if (bars.some((bar) => bar.bucket === 'UNATTRIBUTED')) return bars
  const target = bars.find((bar) => bar.bucket === 'TARGET')
  const actual = bars.find((bar) => bar.bucket === 'ACTUAL')
  if (!target || !actual) return bars
  const losses = bars
    .filter((bar) => bar.bucket !== 'TARGET' && bar.bucket !== 'ACTUAL')
    .reduce((total, bar) => total + bar.tonnes, 0)
  const residual = actual.tonnes - target.tonnes - losses
  if (residual === 0) return bars
  const residualBar: IOpsWaterfallBar = {
    label: 'Unattributed — variance not reconciled to a cause',
    tonnes: residual,
    bucket: 'UNATTRIBUTED',
    drillPath: null,
  }
  const actualIndex = bars.indexOf(actual)
  return [...bars.slice(0, actualIndex), residualBar, ...bars.slice(actualIndex)]
}

export const LossWaterfall = ({ bars }: { bars: IOpsWaterfallBar[] }) => {
  const navigate = useNavigate()
  const allBars = withResidual(bars)
  const target =
    allBars.find((bar) => bar.bucket === 'TARGET')?.tonnes ??
    Math.max(...allBars.map((bar) => Math.abs(bar.tonnes)), 1)
  let running = target

  return (
    <WaterfallWrap>
      {allBars.map((bar, index) => {
        const tone = barTone(bar, target)
        const isUnattributed = bar.bucket === 'UNATTRIBUTED'
        const meta =
          bar.bucket !== 'TARGET' && bar.bucket !== 'ACTUAL' && bar.bucket !== 'UNATTRIBUTED'
            ? LOSS_BUCKET_META[bar.bucket]
            : null
        let left = 0
        let width = 100
        if (meta || isUnattributed) {
          const before = running
          running += bar.tonnes
          left = Math.max((running / target) * 100, 0)
          width = Math.max(((before - running) / target) * 100, 0)
        } else if (bar.bucket === 'ACTUAL') {
          width = Math.min(Math.max((bar.tonnes / target) * 100, 0), 100)
        }
        const segmentTitle = meta ? meta.definition : isUnattributed ? UNATTRIBUTED_TOOLTIP : bar.label
        const handleOpen = () => {
          if (bar.drillPath) navigate(bar.drillPath)
        }
        return (
          <WaterfallRow
            key={`${bar.bucket}-${index}`}
            $clickable={!!bar.drillPath}
            onClick={bar.drillPath ? handleOpen : undefined}
          >
            <LabelCell>
              <BodyText weight='600'>{bar.label}</BodyText>
              {meta && <BucketTag title={meta.definition}>{meta.label}</BucketTag>}
              {isUnattributed && <BucketTag title={UNATTRIBUTED_TOOLTIP}>Unattributed</BucketTag>}
            </LabelCell>
            <Track>
              <Segment
                $left={left}
                $width={width}
                $tone={tone}
                $inset={isUnattributed}
                title={segmentTitle}
              />
            </Track>
            <ValueCol>
              <ValueText $tone={tone} $muted={isUnattributed} title={isUnattributed ? UNATTRIBUTED_TOOLTIP : undefined}>
                {tonnesLabel(bar.tonnes)}
              </ValueText>
              {bar.drillPath && <OpenHint>Open ›</OpenHint>}
            </ValueCol>
          </WaterfallRow>
        )
      })}
    </WaterfallWrap>
  )
}
